import type { VisibilityScope } from "@/components/agentic-v2/VisibilityFilter";
import type { ClientsOnboardingScope, TaskBucket } from "../types";

/** TanStack Query keys for Mission Control — keep serialisable; used for patch + invalidate. */

/** Visibility scope shared by feed, buckets, and missions queries. */
export interface MissionControlScope {
  visibility: VisibilityScope;
  asUserId?: string;
}

/** Inputs that identify one cached multi-bucket snapshot (``GET /mission-control/buckets``). */
export interface MissionControlBucketsKeyInput extends MissionControlScope {
  buckets: TaskBucket[];
  limitPerBucket: number;
}

/** Serialisable query-key factory for Mission Control TanStack Query caches. */
export const missionControlKeys = {
  all: ["mission-control"] as const,
  feedStats: (scope: MissionControlScope) =>
    [...missionControlKeys.all, "feed-stats", scope] as const,
  feed: (scope: MissionControlScope) =>
    [...missionControlKeys.all, "feed", "cursor", scope] as const,
  buckets: (input: MissionControlBucketsKeyInput) =>
    [...missionControlKeys.all, "buckets", input] as const,
  taskBucket: (input: TaskBucketKeyInput) =>
    [...missionControlKeys.all, "task-bucket", input] as const,
  task: (taskId: string) => ["agentic-v2", "task", taskId] as const,
  taskThread: (taskId: string) => ["agentic-v2", "task-thread", taskId] as const,
  missionContext: (missionId: string) =>
    ["agentic-v2", "mission-context", missionId] as const,
  missionsPage: (input: MissionsPageKeyInput) =>
    [...missionControlKeys.all, "missions-page", input] as const,
  companyArtifacts: (input: CompanyArtifactsKeyInput) =>
    [...missionControlKeys.all, "company-artifacts", input] as const,
  pipelineMissions: (input: PipelineMissionsKeyInput) =>
    [...missionControlKeys.all, "pipeline-missions", input] as const,
  missionDetail: (missionId: string) =>
    [...missionControlKeys.all, "mission-detail", missionId] as const,
  missionStepLog: (missionId: string) =>
    [...missionControlKeys.all, "mission-step-log", missionId] as const,
  missionArtifacts: (missionId: string) =>
    [...missionControlKeys.all, "mission-artifacts", missionId] as const,
  missionChannel: (missionId: string) =>
    [...missionControlKeys.all, "mission-channel", missionId] as const,
};

/** Cache key inputs for one Mission Control task bucket (cursor list). */
export interface TaskBucketKeyInput {
  bucket: TaskBucket;
  visibility: VisibilityScope;
  asUserId?: string | null;
  q?: string;
  missionId?: string | null;
}

/** Cache key inputs for the missions browser list (filters + caseload). */
export interface MissionsPageKeyInput {
  filtersKey: string;
  visibility: VisibilityScope;
  asUserId?: string | null;
}

/** Cache key inputs for the Mission Control company artefacts tab. */
export interface CompanyArtifactsKeyInput {
  search: string;
  visibility: VisibilityScope;
  asUserId?: string | null;
}

/** Cache key inputs for the Mission Control pipeline (kanban) missions list. */
export interface PipelineMissionsKeyInput {
  templateId: string;
  visibility: VisibilityScope;
  asUserId?: string | null;
  q: string;
}

/** Inputs that identify one cached clients list page. */
export interface ClientsPageKeyInput {
  filtersKey: string;
  visibility: VisibilityScope;
  /** Tenant guard so caches cannot leak across company switches. */
  companyId?: string;
}

/** Inputs that identify one cached client-scoped task list. */
export interface ClientTasksKeyInput {
  faClientId: string;
  visibility: VisibilityScope;
  /** Tenant guard so caches cannot leak across company switches. */
  companyId?: string;
}

/** Tenant suffix for per-household client caches (matches clients list page). */
function clientTenantKey(companyId?: string) {
  return companyId ?? "";
}

/** Inputs for the paraplanner data-gathering book list. */
export interface DataGatheringBookKeyInput {
  bucket: "upcoming" | "historical";
  visibility: VisibilityScope;
}

/** Inputs that identify one cached leads list page. */
export interface LeadsPageKeyInput {
  filtersKey: string;
  /** Tenant guard so caches cannot leak across company switches. */
  companyId?: string;
}

/** Serialisable query-key factory for Clients app TanStack Query caches. */
export const clientsKeys = {
  all: ["clients"] as const,
  page: (input: ClientsPageKeyInput) =>
    [...clientsKeys.all, "page", input] as const,
  detail: (faClientId: string, companyId?: string) =>
    [...clientsKeys.all, "detail", faClientId, clientTenantKey(companyId)] as const,
  missions: (faClientId: string, companyId?: string) =>
    [...clientsKeys.all, "missions", faClientId, clientTenantKey(companyId)] as const,
  tasks: (input: ClientTasksKeyInput) =>
    [...clientsKeys.all, "tasks", input, clientTenantKey(input.companyId)] as const,
  timeline: (faClientId: string, companyId?: string) =>
    [...clientsKeys.all, "timeline", faClientId, clientTenantKey(companyId)] as const,
  artifacts: (faClientId: string, companyId?: string) =>
    [...clientsKeys.all, "artifacts", faClientId, clientTenantKey(companyId)] as const,
  insight: (faClientId: string, companyId?: string) =>
    [...clientsKeys.all, "insight", faClientId, clientTenantKey(companyId)] as const,
  dataGathering: (faClientId: string, companyId?: string) =>
    [...clientsKeys.all, "data-gathering", faClientId, clientTenantKey(companyId)] as const,
  narrativeSource: (faClientId: string, missionId: string | null, companyId?: string) =>
    [...clientsKeys.all, "narrative-source", faClientId, missionId, clientTenantKey(companyId)] as const,
  dataGatheringBook: (input: DataGatheringBookKeyInput) =>
    [...clientsKeys.all, "data-gathering-book", input] as const,
  stageSummary: (visibility: VisibilityScope, onboardingScope: ClientsOnboardingScope) =>
    [...clientsKeys.all, "stage-summary", visibility, onboardingScope] as const,
};

/** Serialisable query-key factory for Leads app TanStack Query caches. */
export const leadsKeys = {
  all: ["leads"] as const,
  page: (input: LeadsPageKeyInput) =>
    [...leadsKeys.all, "page", input] as const,
  statuses: (companyId?: string) =>
    [...leadsKeys.all, "statuses", companyId ?? ""] as const,
};

/** Inputs that identify one cached Pulse V2 view page. */
export interface PulseViewKeyInput {
  viewId: string;
  paramsKey: string;
}

/** Serialisable query-key factory for Pulse V2 workstack TanStack Query caches. */
export const pulseV2Keys = {
  all: ["pulse-v2"] as const,
  catalog: () => [...pulseV2Keys.all, "catalog"] as const,
  dashboardCatalog: () => [...pulseV2Keys.all, "dashboard-catalog"] as const,
  view: (input: PulseViewKeyInput) =>
    [...pulseV2Keys.all, "view", input] as const,
  dashboard: (dashboardId: string, paramsKey: string) =>
    [...pulseV2Keys.all, "dashboard", dashboardId, paramsKey] as const,
  journeyParity: (scope: string) =>
    [...pulseV2Keys.all, "journey-parity", scope] as const,
  assignees: (viewId: string) =>
    [...pulseV2Keys.all, "assignees", viewId] as const,
  packAvailability: () => [...pulseV2Keys.all, "pack-availability"] as const,
  miDashboards: (packId: string) =>
    [...pulseV2Keys.all, "mi-dashboards", packId] as const,
  miDashboard: (packId: string, dashboardId: string) =>
    [...pulseV2Keys.all, "mi-dashboard", packId, dashboardId] as const,
  miBriefing: (packId: string) =>
    [...pulseV2Keys.all, "mi-briefing", packId] as const,
};
